import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Empty, Row, Skeleton, Table, Tag, Tooltip } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import ReactECharts from "echarts-for-react";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { useWbsName } from "../../shared/useWbsCodes";
import {
  CHART_COLORS,
  ExecutionDetailTab,
  ExecutionMonthColumn,
  fmtINRSigned,
  fmtMonthLong,
  fmtQty,
  num,
} from "./executionShared";
import {
  AXIS,
  CATEGORY_AXIS,
  LEGEND,
  MONEY_AXIS,
  TOOLTIP,
  deviationMoneyCell,
  earnedMoneyCell,
  moneyCell,
  signedCell,
} from "./executionChartKit";

// Month drill-down panels for the two Monthly Execution charts. Clicking a
// month in either graph swaps the all-months view for that month's
// composition — in place, inside the expanded analysis modal.
//
//   Chart 1 (Target vs Executed + Carry-over Backlog)
//     → the backlog BRIDGE (opening → target → executed → closing, the
//       identity computeWaterfall() enforces) + activity-wise contribution
//       ranked worst-shortfall first.
//   Chart 2 (3M Outflow)
//     → the month's Material/Manpower/Machinery split + its top item
//       contributors, planned vs earned with a signed deviation.
//
// COST vs BILLING — the trap this file must not fall into: in an
// ExecutionMonthColumn the summary handler re-derives targetAmount as the sum
// of the 3M pillars, so targetAmount ≡ plannedOutflow (COST). At activity/task
// level targetAmount is still the BILLING value (qty × client rate) and the
// cost lives in plannedOutflow/earnedOutflow. The activity drill therefore
// plots plannedOutflow/earnedOutflow — anything else would not sum back to the
// bar that was clicked.

const TOP_N_CHART = 8;
const TOP_N_TABLE = 12;

// ------------------------------------------------------------------ fetching

export type DrillKind = "activities" | "items";

export interface DrillFilters {
  phaseId?: number;
  activityId?: number;
}

export interface ActivityRollupRow {
  activityId: number;
  activityName: string;
  taskCount: number;
  targetAmount: string;   // BILLING — not used here, see the header note
  executedAmount: string; // BILLING
  plannedOutflow: string; // COST — this is what the chart bars carry
  earnedOutflow: string;  // COST
  outflowDeviation: string;
}

export interface MonthDetailItems {
  materials: any[];
  manpower: any[];
  machinery: any[];
}

// One fetch per (kind, month, phase, activity), cached for the life of the
// modal so going back and re-drilling the same month is instant. The cache is
// dropped whenever the report reloads (a refresh hands down a new months
// array), so a drill can never show figures older than the chart behind it.
export const useMonthDrillData = (
  projectId: number | string | undefined,
  month: string | null,
  filters: DrillFilters,
  kind: DrillKind,
  cacheEpoch: unknown
) => {
  const cache = useRef(new Map<string, any>());
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    cache.current.clear();
  }, [cacheEpoch, projectId]);

  const { phaseId, activityId } = filters;

  useEffect(() => {
    if (!projectId || !month) {
      setData(null);
      return;
    }
    const key = [kind, month, phaseId ?? "", activityId ?? ""].join("|");
    const cached = cache.current.get(key);
    if (cached) {
      setData(cached);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const request =
      kind === "activities"
        ? // Rollup-only call: the same shape MonthDetailPage uses to get the
          // activity rollup without paying for a page of task rows.
          threeMReportApis.getMonthlyExecutionMonthTasks(projectId, month, {
            phaseId,
            activityId,
            page: 1,
            pageSize: 1,
          })
        : threeMReportApis.getMonthlyExecutionMonthDetail(projectId, month, {
            phaseId,
            activityId,
          });
    request
      .then((response: any) => {
        if (cancelled) return;
        if (response?.status) {
          const payload =
            kind === "activities"
              ? ((response?.data?.activityRollup || []) as ActivityRollupRow[])
              : ({
                  materials: response?.data?.materials || [],
                  manpower: response?.data?.manpower || [],
                  machinery: response?.data?.machinery || [],
                } as MonthDetailItems);
          cache.current.set(key, payload);
          setData(payload);
        } else {
          setError(response?.message || "Failed to load the month breakdown");
        }
      })
      .catch(() => !cancelled && setError("Failed to load the month breakdown"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, month, phaseId, activityId, kind]);

  return { data, loading, error };
};

// -------------------------------------------------------------------- header

export const DrillHeader: React.FC<{
  month: string;
  onBack: () => void;
  onOpenMonthDetail?: () => void;
  caption?: string;
}> = ({ month, onBack, onOpenMonthDetail, caption }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
      marginBottom: 12,
      paddingBottom: 10,
      borderBottom: "1px solid #eef1f6",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
      <Button
        type="link"
        size="small"
        icon={<ArrowLeftOutlined />}
        style={{ paddingLeft: 0 }}
        onClick={onBack}
      >
        All months
      </Button>
      <span style={{ color: "#c9d2e3" }}>›</span>
      <span style={{ fontWeight: 600, color: "#022c57" }}>{fmtMonthLong(month)}</span>
      {caption ? (
        <span style={{ color: "#8094ae", fontSize: 12.5, marginLeft: 4 }}>{caption}</span>
      ) : null}
    </div>
    {onOpenMonthDetail ? (
      <Button
        type="link"
        size="small"
        style={{ marginLeft: "auto" }}
        onClick={onOpenMonthDetail}
      >
        Open full month detail <ArrowRightOutlined />
      </Button>
    ) : null}
  </div>
);

const NoActualsNote: React.FC<{ month: string }> = ({ month }) => (
  <Alert
    type="info"
    showIcon
    style={{ marginBottom: 12 }}
    message={`No actuals captured for ${fmtMonthLong(month)} yet`}
    description="The planned side below is real; executed, variance and backlog light up once monthly actuals are recorded for this month's tasks."
  />
);

// --------------------------------------------------------------- the bridge

// Opening → Target (+) → Executed (−) → Closing, the waterfall identity from
// monthly-execution.helper.ts: closing = opening + target − executed.
//
// Drawn with a `custom` series rather than the usual transparent-placeholder
// stack: opening/closing go NEGATIVE when the project runs ahead of plan, and
// ECharts stacks negatives downward from zero independently of positives, so
// the placeholder trick renders a wrong bar for exactly the case worth seeing.
// renderItem draws each bar from its own start value to its own end value.
export const BacklogBridgeChart: React.FC<{ month: ExecutionMonthColumn }> = ({
  month,
}) => {
  const opening = num(month.openingBacklog);
  const target = num(month.targetAmount);
  const executed = num(month.executedAmount);
  const closing = num(month.closingBacklog);

  // Past the actualization boundary computeWaterfall() zeroes opening/variance/
  // closing, so drawing the full bridge would assert "closing backlog ₹0" for a
  // month that simply has no actuals. Collapse to the one bar that is real.
  const bars = useMemo(
    () =>
      month.isActualized
        ? [
            { label: "Opening Backlog", start: 0, end: opening, color: CHART_COLORS.backlog, delta: opening },
            { label: "Planned", start: opening, end: opening + target, color: CHART_COLORS.plan, delta: target },
            { label: "Executed", start: opening + target, end: closing, color: CHART_COLORS.actual, delta: -executed },
            { label: "Closing Backlog", start: 0, end: closing, color: CHART_COLORS.backlog, delta: closing },
          ]
        : [{ label: "Planned", start: 0, end: target, color: CHART_COLORS.plan, delta: target }],
    [month.isActualized, opening, target, executed, closing]
  );

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: "item" as const,
        borderColor: "#e8edf4",
        textStyle: { fontSize: 12 },
        formatter: (params: any) => {
          const bar = bars[params.dataIndex];
          if (!bar) return "";
          const isLevel = bar.label.includes("Backlog");
          return `<b>${bar.label}</b><br/>${
            isLevel ? fmtINRSigned(bar.end, true) : fmtINRSigned(bar.delta, true)
          }`;
        },
      },
      grid: { left: 66, right: 20, top: 28, bottom: 28 },
      xAxis: { ...CATEGORY_AXIS, data: bars.map((bar) => bar.label) },
      yAxis: MONEY_AXIS,
      series: [
        {
          type: "custom" as const,
          name: "Bridge",
          renderItem: (params: any, api: any) => {
            const index = api.value(0);
            const from = api.coord([index, api.value(1)]);
            const to = api.coord([index, api.value(2)]);
            const band = api.size([1, 0])[0];
            const width = Math.min(band * 0.45, 48);
            const top = Math.min(from[1], to[1]);
            // 2px floor so a zero-height bar (no backlog) still reads as a mark.
            const height = Math.max(Math.abs(to[1] - from[1]), 2);
            const bar = bars[index];
            return {
              type: "rect",
              shape: { x: from[0] - width / 2, y: top, width, height, r: [3, 3, 3, 3] },
              style: { fill: bar.color },
              textConfig: { position: "top" },
              textContent: {
                type: "text",
                style: {
                  text: bar.label.includes("Backlog")
                    ? fmtINRSigned(bar.end)
                    : fmtINRSigned(bar.delta),
                  fill: "#526484",
                  fontSize: 11,
                  fontWeight: 500,
                },
              },
            };
          },
          encode: { x: 0, y: [1, 2] },
          data: bars.map((bar, index) => [index, bar.start, bar.end]),
        },
      ],
    }),
    [bars]
  );

  return <ReactECharts option={option} style={{ height: 300 }} notMerge />;
};

// ------------------------------------------------- chart 1: activity drill

interface RankedActivity {
  key: string;
  activityId: number;
  name: string;
  taskCount: number;
  planned: number;
  earned: number;
  variance: number | null;
  pct: number | null;
}

// Ranked by shortfall (most behind first) once actuals exist; by planned cost
// otherwise — a future month has nothing to be behind on. Shared by the chart
// and the table so the two can sit in different layout slots without either
// re-deriving (or re-ordering) the rows.
const useRankedActivities = (
  month: ExecutionMonthColumn,
  rows: ActivityRollupRow[]
): RankedActivity[] => {
  const wbsName = useWbsName();
  const actualized = month.isActualized;
  return useMemo(() => {
    const mapped = rows.map((row) => {
      const planned = num(row.plannedOutflow);
      const earned = actualized ? num(row.earnedOutflow) : 0;
      return {
        key: String(row.activityId),
        activityId: row.activityId,
        name: wbsName(row.activityName),
        taskCount: row.taskCount,
        planned,
        earned,
        variance: actualized ? earned - planned : null,
        pct: actualized && planned > 0 ? (earned / planned) * 100 : null,
      };
    });
    return actualized
      ? mapped.sort((a, b) => (a.variance ?? 0) - (b.variance ?? 0))
      : mapped.sort((a, b) => b.planned - a.planned);
  }, [rows, actualized, wbsName]);
};

const ActivityContributionChart: React.FC<{
  ranked: RankedActivity[];
  actualized: boolean;
}> = ({ ranked, actualized }) => {
  const chartOption = useMemo(() => {
    // Horizontal bars render index 0 at the BOTTOM — reverse so the worst
    // shortfall sits at the top of the chart.
    const top = ranked.slice(0, TOP_N_CHART).reverse();
    return {
      tooltip: TOOLTIP,
      legend: LEGEND,
      grid: { left: 8, right: 24, top: 12, bottom: 40, containLabel: true },
      xAxis: MONEY_AXIS,
      yAxis: {
        ...CATEGORY_AXIS,
        data: top.map((row) => row.name),
        axisLabel: {
          ...AXIS.axisLabel,
          width: 190,
          overflow: "truncate" as const,
        },
      },
      series: [
        {
          name: "Planned Expenditure",
          type: "bar",
          data: top.map((row) => row.planned),
          itemStyle: { color: CHART_COLORS.plan, borderRadius: [0, 3, 3, 0] },
          barMaxWidth: 14,
        },
        {
          name: "Executed Expenditure",
          type: "bar",
          data: top.map((row) => (actualized ? row.earned : null)),
          itemStyle: { color: CHART_COLORS.actual, borderRadius: [0, 3, 3, 0] },
          barMaxWidth: 14,
        },
      ],
    };
  }, [ranked, actualized]);

  return (
    <>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: "#022c57", marginBottom: 4 }}>
        {actualized ? "Who caused the gap" : "Where the plan sits"}
        <span style={{ color: "#8094ae", fontWeight: 400, marginLeft: 8 }}>
          {actualized
            ? `activity-wise target vs executed, most behind first (top ${Math.min(
                TOP_N_CHART,
                ranked.length
              )} of ${ranked.length})`
            : `activity-wise planned cost, largest first (top ${Math.min(
                TOP_N_CHART,
                ranked.length
              )} of ${ranked.length})`}
        </span>
      </div>
      <ReactECharts
        option={chartOption}
        style={{ height: Math.max(200, Math.min(TOP_N_CHART, ranked.length) * 34 + 70) }}
        notMerge
      />
    </>
  );
};

// Full page width — activity names are long and there are six money columns,
// so this table gets the whole row rather than sharing it with the bridge.
const ActivityContributionTable: React.FC<{
  ranked: RankedActivity[];
  actualized: boolean;
}> = ({ ranked, actualized }) => (
  <>
    <div
      style={{
        fontSize: 12.5,
        fontWeight: 600,
        color: "#022c57",
        margin: "16px 0 4px",
      }}
    >
      All activities
      <span style={{ color: "#8094ae", fontWeight: 400, marginLeft: 8 }}>
        {ranked.length} in this month
      </span>
    </div>
    <Table
      size="small"
      rowKey="key"
      dataSource={ranked}
      pagination={
        ranked.length > TOP_N_TABLE
          ? { pageSize: TOP_N_TABLE, showSizeChanger: false, size: "small" }
          : false
      }
      scroll={{ x: 780 }}
      columns={[
        {
          title: "Activity",
          dataIndex: "name",
          key: "name",
          ellipsis: true,
          render: (value: string, row: RankedActivity) => (
            <span style={{ fontWeight: 500, color: "#022c57" }}>
              {value}
              <span style={{ color: "#a9b6c9", fontWeight: 400, marginLeft: 8 }}>
                {row.taskCount} {row.taskCount === 1 ? "task" : "tasks"}
              </span>
            </span>
          ),
        },
        {
          title: "Planned Exp.",
          dataIndex: "planned",
          key: "planned",
          width: 125,
          align: "right" as const,
          render: (value: number) => moneyCell(value),
        },
        {
          title: "Executed Exp.",
          dataIndex: "earned",
          key: "earned",
          width: 130,
          align: "right" as const,
          render: (value: number) => (actualized ? earnedMoneyCell(value) : moneyCell(0)),
        },
        {
          title: "Variance",
          dataIndex: "variance",
          key: "variance",
          width: 120,
          align: "right" as const,
          render: (value: number | null) =>
            value === null ? (
              <span style={{ color: "#c9d2e3" }}>—</span>
            ) : (
              signedCell(value, true)
            ),
        },
        {
          title: "Exec %",
          dataIndex: "pct",
          key: "pct",
          width: 90,
          align: "right" as const,
          render: (value: number | null) =>
            value === null ? (
              <span style={{ color: "#c9d2e3" }}>—</span>
            ) : (
              <span
                style={{
                  fontVariantNumeric: "tabular-nums",
                  color: value >= 99.5 ? CHART_COLORS.positive : "#526484",
                }}
              >
                {value.toFixed(1)}%
              </span>
            ),
        },
      ]}
    />
  </>
);

// Two charts share the top row; the activity table gets the full page width
// below them.
export const TargetExecutedMonthDrill: React.FC<{
  month: ExecutionMonthColumn;
  rows: ActivityRollupRow[];
  loading: boolean;
  error?: string | null;
}> = ({ month, rows, loading, error }) => {
  const ranked = useRankedActivities(month, rows);
  const actualized = month.isActualized;

  const activityState = loading ? (
    <Skeleton active paragraph={{ rows: 6 }} />
  ) : error ? (
    <Empty description={error} style={{ padding: "32px 0" }} />
  ) : !ranked.length ? (
    <Empty
      description="No activities scheduled or executed in this month"
      style={{ padding: "32px 0" }}
    />
  ) : null;

  return (
    <>
      {!actualized && <NoActualsNote month={month.month} />}
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={10}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "#022c57", marginBottom: 4 }}>
            {actualized ? "Carry-over bridge" : "Month plan"}
            <span style={{ color: "#8094ae", fontWeight: 400, marginLeft: 8 }}>
              {actualized
                ? "opening + planned − executed = closing"
                : "no backlog to bridge until actuals land"}
            </span>
          </div>
          <BacklogBridgeChart month={month} />
        </Col>
        <Col xs={24} xl={14}>
          {activityState || (
            <ActivityContributionChart ranked={ranked} actualized={actualized} />
          )}
        </Col>
      </Row>
      {!activityState && (
        <ActivityContributionTable ranked={ranked} actualized={actualized} />
      )}
    </>
  );
};

// --------------------------------------------------- chart 2: outflow drill

export type OutflowPillar = "total" | "material" | "manpower" | "machinery";

const PILLARS: Array<{
  key: Exclude<OutflowPillar, "total">;
  label: string;
  planned: keyof ExecutionMonthColumn;
  earned: keyof ExecutionMonthColumn;
  bucket: keyof MonthDetailItems;
  tab: ExecutionDetailTab;
}> = [
  { key: "material", label: "Material", planned: "plannedMaterial", earned: "earnedMaterial", bucket: "materials", tab: "materials" },
  { key: "manpower", label: "Manpower", planned: "plannedManpower", earned: "earnedManpower", bucket: "manpower", tab: "manpower" },
  { key: "machinery", label: "Machinery", planned: "plannedMachinery", earned: "earnedMachinery", bucket: "machinery", tab: "machinery" },
];

// The month's 3M split, straight off the clicked column — no fetch, and it sums
// to the Total bar exactly because it IS the same object.
export const PillarSplitChart: React.FC<{ month: ExecutionMonthColumn }> = ({ month }) => {
  const option = useMemo(() => {
    // Reversed so Material sits at the top of the horizontal chart.
    const rows = [...PILLARS].reverse();
    return {
      tooltip: TOOLTIP,
      legend: LEGEND,
      grid: { left: 8, right: 24, top: 12, bottom: 40, containLabel: true },
      xAxis: MONEY_AXIS,
      yAxis: { ...CATEGORY_AXIS, data: rows.map((pillar) => pillar.label) },
      series: [
        {
          name: "Planned Expenditure",
          type: "bar",
          data: rows.map((pillar) => num(month[pillar.planned] as string)),
          itemStyle: { color: CHART_COLORS.plan, borderRadius: [0, 3, 3, 0] },
          barMaxWidth: 18,
        },
        {
          name: "Executed Expenditure",
          type: "bar",
          data: rows.map((pillar) =>
            month.isActualized ? num(month[pillar.earned] as string) : null
          ),
          itemStyle: { color: CHART_COLORS.actual, borderRadius: [0, 3, 3, 0] },
          barMaxWidth: 18,
        },
      ],
    };
  }, [month]);

  return <ReactECharts option={option} style={{ height: 240 }} notMerge />;
};

interface ContributorRow {
  key: string;
  name: string;
  meta: string;
  pillar: string;
  planned: number;
  earned: number;
  plannedQty: number;
  earnedQty: number;
  uom: string;
}

// Item identity per pillar — mirrors MonthDetailPage's breakdown naming so the
// drill and the full month page call the same thing by the same name.
const contributorsOf = (
  items: MonthDetailItems | null,
  pillar: OutflowPillar
): ContributorRow[] => {
  if (!items) return [];
  const shape = (row: any, index: number, pillarLabel: string, prefix: string): ContributorRow => {
    const isMaterial = prefix === "mat";
    const name = isMaterial
      ? row.materialName || "Unlinked material"
      : row.itemName || "Unlinked item";
    const code = isMaterial ? row.materialCode : row.itemCode;
    const extra = prefix === "man" ? row.source : prefix === "mac" ? row.head : "";
    return {
      key: `${prefix}-${index}`,
      name,
      meta: [code, extra].filter(Boolean).join(" · "),
      pillar: pillarLabel,
      planned: num(row.monthCost),
      earned: num(row.earnedCost),
      plannedQty: num(row.monthQuantity),
      earnedQty: num(row.earnedQuantity),
      uom: row.uomName || "",
    };
  };
  const buckets: Array<[keyof MonthDetailItems, string, string]> =
    pillar === "total"
      ? [
          ["materials", "Material", "mat"],
          ["manpower", "Manpower", "man"],
          ["machinery", "Machinery", "mac"],
        ]
      : [
          [
            PILLARS.find((p) => p.key === pillar)!.bucket,
            PILLARS.find((p) => p.key === pillar)!.label,
            pillar === "material" ? "mat" : pillar === "manpower" ? "man" : "mac",
          ],
        ];
  // The backend already returns each bucket sorted by cost desc (byCost in the
  // month-detail handler); a union across buckets still needs one merge sort.
  const rows = buckets.flatMap(([bucket, label, prefix]) =>
    (items[bucket] || []).map((row: any, index: number) => shape(row, index, label, prefix))
  );
  return buckets.length === 1
    ? rows
    : rows.sort((a, b) => Math.max(b.planned, b.earned) - Math.max(a.planned, a.earned));
};

export const TopContributorsPanel: React.FC<{
  month: ExecutionMonthColumn;
  items: MonthDetailItems | null;
  pillar: OutflowPillar;
  loading: boolean;
  error?: string | null;
  showChart: boolean;
}> = ({ month, items, pillar, loading, error, showChart }) => {
  const rows = useMemo(() => contributorsOf(items, pillar), [items, pillar]);
  const actualized = month.isActualized;

  const chartOption = useMemo(() => {
    const top = rows.slice(0, TOP_N_CHART).reverse();
    return {
      tooltip: TOOLTIP,
      legend: LEGEND,
      grid: { left: 8, right: 24, top: 12, bottom: 40, containLabel: true },
      xAxis: MONEY_AXIS,
      yAxis: {
        ...CATEGORY_AXIS,
        data: top.map((row) => row.name),
        axisLabel: { ...AXIS.axisLabel, width: 190, overflow: "truncate" as const },
      },
      series: [
        {
          name: "Planned Expenditure",
          type: "bar",
          data: top.map((row) => row.planned),
          itemStyle: { color: CHART_COLORS.plan, borderRadius: [0, 3, 3, 0] },
          barMaxWidth: 14,
        },
        {
          name: "Executed Expenditure",
          type: "bar",
          data: top.map((row) => (actualized ? row.earned : null)),
          itemStyle: { color: CHART_COLORS.actual, borderRadius: [0, 3, 3, 0] },
          barMaxWidth: 14,
        },
      ],
    };
  }, [rows, actualized]);

  if (loading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (error) return <Empty description={error} style={{ padding: "32px 0" }} />;
  if (!rows.length) {
    return <Empty description="Nothing in this month" style={{ padding: "32px 0" }} />;
  }

  return (
    <>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: "#022c57", marginBottom: 4 }}>
        Top contributors
        <span style={{ color: "#8094ae", fontWeight: 400, marginLeft: 8 }}>
          largest {pillar === "total" ? "3M" : pillar} lines this month ({
            Math.min(TOP_N_TABLE, rows.length)
          } of {rows.length})
        </span>
      </div>
      {showChart && (
        <ReactECharts
          option={chartOption}
          style={{ height: Math.max(200, Math.min(TOP_N_CHART, rows.length) * 34 + 70) }}
          notMerge
        />
      )}
      <Table
        size="small"
        style={{ marginTop: showChart ? 12 : 0 }}
        rowKey="key"
        dataSource={rows.slice(0, TOP_N_TABLE)}
        pagination={false}
        scroll={{ x: 760 }}
        columns={[
          {
            title: "Item",
            dataIndex: "name",
            key: "name",
            ellipsis: true,
            render: (value: string, row: ContributorRow) => (
              <Tooltip
                title={
                  <>
                    Planned {fmtQty(row.plannedQty)} {row.uom}
                    <br />
                    Earned {fmtQty(row.earnedQty)} {row.uom}
                  </>
                }
              >
                <span style={{ fontWeight: 500, color: "#022c57" }}>
                  {value}
                  {row.meta ? (
                    <span style={{ color: "#a9b6c9", fontWeight: 400, marginLeft: 8 }}>
                      {row.meta}
                    </span>
                  ) : null}
                </span>
              </Tooltip>
            ),
          },
          ...(pillar === "total"
            ? [
                {
                  title: "Pillar",
                  dataIndex: "pillar",
                  key: "pillar",
                  width: 110,
                  render: (value: string) => (
                    <Tag
                      style={{
                        background: "#f8fafc",
                        borderColor: "#e2e8f0",
                        color: "#526484",
                        fontSize: 11,
                      }}
                    >
                      {value}
                    </Tag>
                  ),
                },
              ]
            : []),
          {
            title: "Planned",
            dataIndex: "planned",
            key: "planned",
            width: 120,
            align: "right" as const,
            render: (value: number) => moneyCell(value),
          },
          {
            title: "Executed",
            dataIndex: "earned",
            key: "earned",
            width: 120,
            align: "right" as const,
            render: (value: number) => (actualized ? earnedMoneyCell(value) : moneyCell(0)),
          },
          {
            title: "Deviation",
            key: "deviation",
            width: 120,
            align: "right" as const,
            render: (_: any, row: ContributorRow) =>
              actualized ? (
                deviationMoneyCell(row.earned - row.planned, row.earned)
              ) : (
                <span style={{ color: "#c9d2e3" }}>—</span>
              ),
          },
        ]}
      />
    </>
  );
};

export const OutflowMonthDrill: React.FC<{
  month: ExecutionMonthColumn;
  items: MonthDetailItems | null;
  pillar: OutflowPillar;
  loading: boolean;
  error?: string | null;
}> = ({ month, items, pillar, loading, error }) => {
  const isTotal = pillar === "total";
  return (
    <>
      {!month.isActualized && <NoActualsNote month={month.month} />}
      {isTotal ? (
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={10}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#022c57", marginBottom: 4 }}>
              3M split
              <span style={{ color: "#8094ae", fontWeight: 400, marginLeft: 8 }}>
                sums to the month's total outflow
              </span>
            </div>
            <PillarSplitChart month={month} />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
                color: "#526484",
                borderTop: "1px solid #eef1f6",
                paddingTop: 8,
                marginTop: 4,
              }}
            >
              <span>Total</span>
              <span>
                {moneyCell(num(month.plannedOutflow), true)}
                <span style={{ color: "#c9d2e3", margin: "0 6px" }}>/</span>
                {month.isActualized ? (
                  earnedMoneyCell(num(month.earnedOutflow))
                ) : (
                  <span style={{ color: "#c9d2e3" }}>—</span>
                )}
              </span>
            </div>
          </Col>
          <Col xs={24} xl={14}>
            <TopContributorsPanel
              month={month}
              items={items}
              pillar={pillar}
              loading={loading}
              error={error}
              showChart={false}
            />
          </Col>
        </Row>
      ) : (
        <TopContributorsPanel
          month={month}
          items={items}
          pillar={pillar}
          loading={loading}
          error={error}
          showChart
        />
      )}
    </>
  );
};

// Which Month Detail tab the "Open full month detail →" link should land on.
export const tabForPillar = (pillar: OutflowPillar): ExecutionDetailTab =>
  PILLARS.find((entry) => entry.key === pillar)?.tab ?? "tasks";
